import React from 'react';
import { t } from '@lingui/core/macro';

import { Box, Flex, Heading } from '@rover/kibble/core';
import useLocationContext from '@rover/react-lib/src/context/useLocationContext';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';

import ContactButton from '../../../components/ContactButton';
import { useRevampCta } from '../../hooks/useRevampCta';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';

import RevampCancellationPolicy from './RevampCancellationPolicy';
import { CTAPropsCommon } from './RevampServicesCta';
import RevampServicesCtaCommon from './RevampServicesCtaCommon';
import { getSitterSlugFromWebUrl } from './utils';

type Props = {
  handleOpenSheet: () => void;
  isOpen: boolean;
};

const RevampServicesCtaOwnerView = ({
  priceTransparencyLevel,
  locale,
  petType,
  petCount,
  bookingFeePercentage,
  handleOpenSheet,
  isOpen,
}: CTAPropsCommon & Props): JSX.Element => {
  const { sitterData, selectedService, cancellationPolicy, isContactUnavailable } = useRevampCta();
  const { contactUrl } = useRevampProfileContext();
  const currentUser = useCurrentUser();
  const location = useLocationContext();

  const sitterSlug = getSitterSlugFromWebUrl(sitterData.webUrl);

  return (
    <Flex data-testid="revamp-services-cta-owner-view" flexDirection="column">
      <Heading
        size="400"
        mb="0x"
        webProps={{ responsive: false }}
        sx={{ letterSpacing: '-0.26px' }}
      >
        {selectedService
          ? t`Contact for ${selectedService.displayName}`
          : t`Contact ${sitterData.shortName}`}
      </Heading>
      <Flex flexDirection="column" gap="4x" pt="6x">
        <RevampServicesCtaCommon
          priceTransparencyLevel={priceTransparencyLevel}
          locale={locale}
          petCount={petCount}
          petType={petType}
          bookingFeePercentage={bookingFeePercentage}
          handleOpenSheet={handleOpenSheet}
          isOpen={isOpen}
          cancellationPolicy={cancellationPolicy}
        />
      </Flex>
      <Box pt="8x" pb="6x">
        <ContactButton
          sitterInfo={sitterData}
          sitterSlug={sitterSlug}
          contactUrl={contactUrl}
          isLoggedIn={currentUser.isAuthenticated}
          location={location}
          pagePosition="sidebar"
          shouldShowRevampLayout
          disabled={isContactUnavailable}
        />
      </Box>

      <RevampCancellationPolicy
        cancellationPolicy={cancellationPolicy}
        serviceName={selectedService?.displayName}
        cancellationPolicyDesc={selectedService?.meta?.cancellationPolicyDesc}
        providerId={sitterData.opk}
      />
    </Flex>
  );
};

export default RevampServicesCtaOwnerView;
